import { SpoolList, formatDate } from '@deizi/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import logo from '../../assets/logo-horizontal-cor.svg';
import { useLogoSrc } from '../../lib/brand-api';
import { PageHead } from '../../components/PageHead';
import { ErrorBox, Skeleton } from '../../components/States';
import { api } from '../../lib/api';
import { formatG } from '../../lib/inventory-api';
import { QrImage, spoolLabelUrl } from './qr';

/**
 * Printable spool labels (QR + code + name), one per spool, laid out for 50 × 30 mm thermal labels
 * and printed through the browser dialog.
 */
export function LabelsPage() {
  const [params] = useSearchParams();
  const logoSrc = useLogoSrc(logo);
  const ids = (params.get('ids') ?? '').split(',').filter(Boolean);
  const spools = useQuery({
    queryKey: ['spools', 'labels', ids.join(',')],
    queryFn: () => api.get(`/spools?limit=200&ids=${ids.join(',')}`, SpoolList),
    enabled: ids.length > 0,
  });
  // "Imprimir ao salvar" (Compra-Carreteis) opens this page with ?imprimir=1: print once the
  // labels and their QR codes are on screen.
  const autoPrint = params.get('imprimir') === '1';
  useEffect(() => {
    if (!autoPrint || !spools.data) return;
    const t = window.setTimeout(() => window.print(), 800);
    return () => window.clearTimeout(t);
  }, [autoPrint, spools.data]);
  return (
    <>
      <style>{`
        @media print {
          @page { size: 50mm 30mm; margin: 0; }
          body * { visibility: hidden; }
          .labels, .labels * { visibility: visible; }
          .labels { position: absolute; inset: 0; display: block !important; }
          .label { page-break-after: always; border: 0 !important; margin: 0 !important; }
        }
        .label { width: 50mm; height: 30mm; box-sizing: border-box; padding: 2mm; display: flex; gap: 2mm;
          align-items: center; border: 1px dashed var(--line); border-radius: 6px; background: #fff; color: #1D1D1D; }
      `}</style>
      <PageHead
        crumb="Filamentos e carretéis"
        title="Etiquetas"
        actions={
          <button type="button" className="btn p" onClick={() => window.print()}>
            Imprimir
          </button>
        }
      />
      <p className="sm muted" style={{ marginBottom: 14 }}>
        Uma etiqueta de 50 × 30 mm por carretel. A câmera do celular abre o carretel pelo QR.
      </p>
      {spools.isPending && ids.length ? <Skeleton rows={2} height={100} /> : null}
      {spools.isError ? (
        <ErrorBox
          title="Não foi possível carregar os carretéis"
          onRetry={() => void spools.refetch()}
        />
      ) : null}
      <div className="labels row wrap" style={{ gap: 12 }}>
        {(spools.data?.data ?? [])
          .toSorted((a, b) => a.code.localeCompare(b.code))
          .map((s) => (
            <div key={s.id} className="label">
              <QrImage text={spoolLabelUrl(s.code)} size={88} />
              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1mm' }}>
                <b style={{ fontSize: 16, fontFamily: 'Fredoka, sans-serif' }}>{s.code}</b>
                <span style={{ fontSize: 10, lineHeight: 1.2 }}>{s.filament.name}</span>
                <span style={{ fontSize: 9, color: '#6B5F57' }}>
                  {formatG(s.initialGrams)}
                  {s.purchase ? ` · ${formatDate(`${s.purchase.purchasedOn}T12:00:00Z`)}` : ''}
                </span>
                <img
                  src={logoSrc}
                  alt="Deizi3d"
                  style={{ height: 10, width: 'auto', alignSelf: 'flex-start' }}
                />
              </div>
            </div>
          ))}
      </div>
    </>
  );
}
